import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2, Plus, Loader2, Save, Trash2, Pencil, Star, CheckCircle2,
  MapPin, Phone, Mail, Hash, Briefcase, User, Palette, Globe, DollarSign, Users,
  History, ArrowRight, Sparkles, GraduationCap, TrendingUp, Layers, Network
} from "lucide-react";
import { COUNTRIES, getLegalForms, SOCIETE_MODULES } from "../lib/legalForms";
import PageHeader from "../components/PageHeader";
import { DateField } from "../components/DateField";
import GroupOrgChart from "../components/GroupOrgChart";
import { KpiSkeleton, ListSkeleton } from "../components/Skeletons";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"];
const DEVISES = [
  { code: "EUR", symbol: "EUR", label: "Euro (EUR)", flag: "EU" },
  { code: "USD", symbol: "$", label: "Dollar US (USD)", flag: "US" },
  { code: "GBP", symbol: "£", label: "Livre Sterling (GBP)", flag: "GB" },
  { code: "CHF", symbol: "CHF", label: "Franc Suisse (CHF)", flag: "CH" },
];

const MODULE_ICONS = { User: User, Briefcase: Briefcase, Sparkles: Sparkles, TrendingUp: TrendingUp, GraduationCap: GraduationCap };

const EMPTY_FORM = {
  name: "", siren: "", siret: "", forme_juridique: "", capital: 0,
  adresse: "", ville: "", code_postal: "", pays: "FR",
  email: "", telephone: "", tva_intra: "", activite: "",
  dirigeant: "", date_creation_societe: "", color: "#3b82f6", devise: "EUR", logo: "",
  type: "filiale", parent_societe_id: "",
  enabled_modules: {
    mon_espace: false,
    force_de_vente: false,
    blitzz_accompaniment: false,
    gestion_activite: false,
    formation: false,
  },
};

export default function SocietesPage() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const { reloadSocietes } = useSociete();

  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editSoc, setEditSoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [tab, setTab] = useState("societes");
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixChanges, setMatrixChanges] = useState({});
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [billing, setBilling] = useState(null);
  const [overview, setOverview] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/societes");
      setSocietes(r.data || []);
    } catch { setSocietes([]); }
    try {
      const b = await api.get("/societes-billing-preview");
      setBilling(b.data);
    } catch { /* not blocking */ }
    try {
      const o = await api.get("/societes-overview");
      setOverview(o.data);
    } catch { /* not blocking */ }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, []);

  const loadMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const r = await api.get("/societe-roles/matrix");
      setMatrix(r.data);
      setMatrixChanges({});
    } catch { setMatrix(null); }
    setMatrixLoading(false);
  }, []);

  useEffect(() => { if (tab === "droits") loadMatrix(); }, [tab]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await api.get("/societe-roles/audit");
      setAudit(r.data || []);
    } catch { setAudit([]); }
    setAuditLoading(false);
  }, []);

  useEffect(() => { if (tab === "historique") loadAudit(); }, [tab]);

  const updateMatrixRole = (userId, socId, role) => {
    setMatrixChanges(c => ({ ...c, [`${userId}_${socId}`]: { user_id: userId, societe_id: socId, role } }));
  };

  const saveMatrix = async () => {
    setSavingMatrix(true);
    try {
      const updates = Object.values(matrixChanges);
      if (updates.length > 0) {
        await api.put("/societe-roles/bulk", { updates });
      }
      setMatrixChanges({});
      loadMatrix();
      toast.success("Rattachements enregistrés");
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); }
    setSavingMatrix(false);
  };

  const openNew = () => {
    setEditSoc(null);
    setForm({ ...EMPTY_FORM, color: COLORS[societes.length % COLORS.length] });
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditSoc(s);
    // Map old "France" / etc. to ISO code if needed
    const country = COUNTRIES.find(c => c.code === s.pays || c.label.toLowerCase() === String(s.pays).toLowerCase()) || COUNTRIES[0];
    setForm({
      name: s.name || "", siren: s.siren || "", siret: s.siret || "",
      forme_juridique: s.forme_juridique || "", capital: s.capital || 0,
      adresse: s.adresse || "", ville: s.ville || "", code_postal: s.code_postal || "",
      pays: country.code, email: s.email || "", telephone: s.telephone || "",
      tva_intra: s.tva_intra || "", activite: s.activite || "",
      dirigeant: s.dirigeant || "", date_creation_societe: s.date_creation_societe || "",
      type: s.type || "filiale", parent_societe_id: s.parent_societe_id || "",
      color: s.color || "#3b82f6", devise: s.devise || "EUR", logo: s.logo || "",
      enabled_modules: s.enabled_modules || { mon_espace: false, force_de_vente: false, blitzz_accompaniment: false, gestion_activite: false, formation: false },
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editSoc) {
        await api.put(`/societes/${editSoc.societe_id}`, form);
      } else {
        await api.post("/societes", form);
      }
      setShowDialog(false);
      reload();
      reloadSocietes();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteSoc = async (sid) => {
    if (!window.confirm("Supprimer cette societe ?")) return;
    try {
      await api.delete(`/societes/${sid}`);
      reload();
      reloadSocietes();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const setDefault = async (sid) => {
    await api.put(`/societes/${sid}/set-default`);
    reload();
    reloadSocietes();
  };

  const F = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const deviseInfo = (code) => DEVISES.find(d => d.code === code) || DEVISES[0];

  const ROLE_OPTIONS = [
    { value: "admin", label: "Admin", color: "#10b981", bg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
    { value: "member", label: "Membre", color: "#3b82f6", bg: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
    { value: "readonly", label: "Lecture", color: "#f59e0b", bg: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    { value: "none", label: "Aucun", color: "#ef4444", bg: "bg-red-500/10 text-red-400 border-red-500/20" },
  ];

  const getRoleDisplay = (role) => ROLE_OPTIONS.find(r => r.value === role) || ROLE_OPTIONS[3];

  const getMatrixRole = (userId, socId) => {
    const key = `${userId}_${socId}`;
    if (matrixChanges[key]) return matrixChanges[key].role;
    const staffRow = matrix?.staff?.find(s => s.user_id === userId);
    return staffRow?.societe_roles?.[socId] || "none";
  };

  const pendingCount = Object.keys(matrixChanges).length;

  if (loading) return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="societes-loading">
      <KpiSkeleton count={4} />
      <ListSkeleton items={6} />
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="societes-page">
      <PageHeader
        icon={<Building2 />}
        title="Gestion des sociétés"
        description={`${societes.length} société(s) — chaque société est un espace indépendant`}
        actions={tab === "societes" ? (
          <Button onClick={openNew} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-sm" data-testid="add-societe-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle société
          </Button>
        ) : null}
        testId="societes-header"
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-[#14101c] rounded-xl p-1 w-fit border border-white/5" data-testid="societes-tabs">
        <button
          onClick={() => setTab("societes")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
            tab === "societes"
              ? "bg-[#f7c935] text-[#0b0811]"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="tab-societes"
        >
          <Building2 className="w-3.5 h-3.5" /> Societes
        </button>
        <button
          onClick={() => setTab("droits")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
            tab === "droits"
              ? "bg-[#f7c935] text-[#0b0811]"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="tab-droits"
        >
          <Users className="w-3.5 h-3.5" /> Matrice des droits
        </button>
        <button
          onClick={() => setTab("organigramme")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
            tab === "organigramme"
              ? "bg-[#f7c935] text-[#0b0811]"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="tab-organigramme"
        >
          <Network className="w-3.5 h-3.5" /> Organigramme
        </button>
        <button
          onClick={() => setTab("historique")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
            tab === "historique"
              ? "bg-[#f7c935] text-[#0b0811]"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="tab-historique"
        >
          <History className="w-3.5 h-3.5" /> Historique
        </button>
      </div>

      {/* Tab: Societes */}
      {tab === "societes" && (
        <>
          {/* Billing summary banner */}
          {billing && (billing.extra_count > 0 || billing.next_societe_extra_monthly > 0) && (
            <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="billing-extra-notice">
              <DollarSign className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-xs">
                <div className="text-amber-300 font-bold flex items-center gap-2 flex-wrap">
                  Offre <span className="text-amber-200">{billing.offer_name}</span>
                  <span className="text-slate-500">•</span>
                  {billing.societe_count} societe{billing.societe_count > 1 ? "s" : ""} sur {billing.included_quota} incluse{billing.included_quota > 1 ? "s" : ""}
                  {billing.extra_count > 0 && billing.unit_fee_monthly > 0 && (
                    <>
                      <span className="text-slate-500">•</span>
                      <span className="text-amber-300">+{billing.current_extra_total_monthly} EUR/mois en frais societes additionnelles</span>
                    </>
                  )}
                </div>
                {billing.next_societe_extra_monthly > 0 && (
                  <div className="text-[10px] text-amber-200/70 mt-1">Toute societe additionnelle au-dela du quota = +{billing.next_societe_extra_monthly} EUR/mois HT</div>
                )}
              </div>
            </div>
          )}

          {/* Multi-societe overview dashboard */}
          {overview && overview.societes && overview.societes.length > 0 ? (
            <OverviewDashboard data={overview} />
          ) : !overview ? (
            <div className="space-y-4 mb-6" data-testid="overview-loading">
              <KpiSkeleton count={4} testId="overview-kpi-skeleton" />
            </div>
          ) : null}

          {societes.length === 0 ? (
            <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-lg text-slate-400 font-medium">Aucune societe configuree</p>
              <p className="text-sm text-slate-500 mt-1 mb-4">Creez votre premiere societe pour commencer</p>
              <Button onClick={openNew} className="bg-[#f7c935] text-[#0b0811]">
                <Plus className="w-4 h-4 mr-1" /> Creer une societe
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {societes.map(s => {
                const dev = deviseInfo(s.devise);
                return (
                  <div key={s.societe_id} className="bg-[#14101c] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors" data-testid={`societe-card-${s.societe_id}`}>
                    <div className="flex items-start gap-4">
                      {s.logo ? (
                        <img src={s.logo} alt="" className="w-12 h-12 rounded-xl object-contain flex-shrink-0 bg-white/5 border border-white/5" data-testid={`societe-logo-${s.societe_id}`} />
                      ) : (
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.color || "#3b82f6"}15` }}>
                          <Building2 className="w-6 h-6" style={{ color: s.color || "#3b82f6" }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-white truncate">{s.name}</span>
                          {s.is_default && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935] font-bold flex items-center gap-0.5 flex-shrink-0">
                              <Star className="w-2.5 h-2.5" /> Defaut
                            </span>
                          )}
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400 flex-shrink-0">
                            {dev.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          {s.forme_juridique && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" /> {s.forme_juridique}</span>}
                          {s.siren && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Hash className="w-2.5 h-2.5" /> {s.siren}</span>}
                          {s.ville && <span className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {s.ville} {s.pays && s.pays !== "France" ? `(${s.pays})` : ""}</span>}
                          {s.dirigeant && <span className="text-[10px] text-slate-500 flex items-center gap-1"><User className="w-2.5 h-2.5" /> {s.dirigeant}</span>}
                          {s.email && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> {s.email}</span>}
                          {s.capital > 0 && <span className="text-[10px] text-slate-500 flex items-center gap-1"><DollarSign className="w-2.5 h-2.5" /> {s.capital.toLocaleString("fr-FR")} {dev.code}</span>}
                          {s.tva_intra && <span className="text-[10px] text-slate-500">TVA {s.tva_intra}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {!s.is_default && (
                          <button onClick={() => setDefault(s.societe_id)} className="p-1.5 hover:bg-[#f7c935]/10 rounded-lg" title="Définir par défaut">
                            <Star className="w-3.5 h-3.5 text-slate-500 hover:text-[#f7c935]" />
                          </button>
                        )}
                        <button onClick={() => navigate(`/admin/societes/${s.societe_id}/builder`)} className="px-2 py-1.5 hover:bg-[#f7c935]/10 rounded-lg flex items-center gap-1 text-[10px] font-bold text-[#f7c935]" title="Builder : identité visuelle, fonctions, panel partenaire" data-testid={`builder-societe-${s.societe_id}`}>
                          <Palette className="w-3.5 h-3.5" /> Builder
                        </button>
                        <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-white/5 rounded-lg" data-testid={`edit-societe-${s.societe_id}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        {societes.length > 1 && (
                          <button onClick={() => deleteSoc(s.societe_id)} className="p-1.5 hover:bg-red-500/10 rounded-lg">
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Tab: Matrice des droits */}
      {/* Tab: Organigramme */}
      {tab === "organigramme" && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-6" data-testid="organigramme-section">
          <div className="mb-5">
            <h3 className="text-sm font-bold text-white">Organigramme du groupe</h3>
            <p className="text-[11px] text-slate-500 mt-1">
              Liens de détention basés sur la « Société mère » de chaque société. Modifiez-les via Sociétés → Modifier.
            </p>
          </div>
          <GroupOrgChart societes={societes} />
        </div>
      )}

      {tab === "droits" && (
        <div data-testid="matrix-section">
          {matrixLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>
          ) : !matrix ? (
            <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400 text-sm">Impossible de charger la matrice</p>
              <Button onClick={loadMatrix} className="mt-3 bg-white/5 text-white text-xs">Reessayer</Button>
            </div>
          ) : (
            <>
              {/* Save bar */}
              {pendingCount > 0 && (
                <div className="flex items-center justify-between bg-[#f7c935]/10 border border-[#f7c935]/20 rounded-xl px-4 py-3 mb-4" data-testid="matrix-save-bar">
                  <span className="text-xs text-[#f7c935] font-medium">
                    {pendingCount} modification{pendingCount > 1 ? "s" : ""} en attente
                  </span>
                  <div className="flex gap-2">
                    <Button onClick={() => setMatrixChanges({})} variant="ghost" className="text-xs text-slate-400 hover:text-white h-8 px-3">
                      Annuler
                    </Button>
                    <Button onClick={saveMatrix} disabled={savingMatrix} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-xs font-bold h-8 px-4" data-testid="save-matrix-btn">
                      {savingMatrix ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              )}

              {/* Legend */}
              <div className="flex flex-wrap gap-3 mb-2">
                {ROLE_OPTIONS.map(r => (
                  <div key={r.value} className={`flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full border ${r.bg}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color }} />
                    {r.label}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mb-4" data-testid="matrix-isolation-note">
                Par défaut (aucun rattachement enregistré), un membre voit toutes les sociétés. Dès qu'un rattachement est
                enregistré pour un membre, il n'accède plus qu'aux sociétés où son rôle n'est pas « Aucun accès ».
              </p>

              {/* Matrix table */}
              <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="roles-matrix-table">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500 font-bold bg-[#0b0811]/50 sticky left-0 z-10 min-w-[200px]">
                          Membre
                        </th>
                        {matrix.societes.map(soc => (
                          <th key={soc.societe_id} className="text-center py-3 px-3 min-w-[130px]">
                            <div className="flex items-center justify-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: soc.color || "#3b82f6" }} />
                              <span className="text-[10px] font-bold text-white truncate">{soc.name}</span>
                            </div>
                            <span className="text-[9px] text-slate-500">{soc.devise || "EUR"}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.staff.map((member, idx) => (
                        <tr key={member.user_id} className={`border-b border-white/[0.03] ${idx % 2 === 0 ? "" : "bg-white/[0.01]"}`} data-testid={`matrix-row-${member.user_id}`}>
                          <td className="py-2.5 px-4 sticky left-0 z-10 bg-[#14101c]">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-[10px] font-bold text-slate-300">
                                {(member.name || member.email || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-white leading-tight">{member.name || member.email?.split("@")[0]}</p>
                                <p className="text-[10px] text-slate-500">{member.email}</p>
                                <span className="text-[9px] font-bold text-slate-400" data-testid={`matrix-role-badge-${member.user_id}`}>
                                  {(member.roles || []).includes("formateur") && member.role === "client"
                                    ? "Formateur"
                                    : member.role === "tenant_member"
                                    ? "Salarié"
                                    : "Admin"}
                                </span>
                              </div>
                            </div>
                          </td>
                          {matrix.societes.map(soc => {
                            const currentRole = getMatrixRole(member.user_id, soc.societe_id);
                            const roleInfo = getRoleDisplay(currentRole);
                            const isChanged = matrixChanges[`${member.user_id}_${soc.societe_id}`];
                            return (
                              <td key={soc.societe_id} className="py-2.5 px-3 text-center">
                                <select
                                  value={currentRole}
                                  onChange={(e) => updateMatrixRole(member.user_id, soc.societe_id, e.target.value)}
                                  className={`text-[11px] font-medium rounded-lg px-2.5 py-1.5 border cursor-pointer transition-all appearance-none text-center w-full ${roleInfo.bg} ${isChanged ? "ring-1 ring-[#f7c935]/50" : ""}`}
                                  style={{ background: "transparent" }}
                                  data-testid={`role-select-${member.user_id}-${soc.societe_id}`}
                                >
                                  {ROLE_OPTIONS.map(r => (
                                    <option key={r.value} value={r.value} className="bg-[#0b0811] text-white">{r.label}</option>
                                  ))}
                                </select>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Info */}
              <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                {matrix.staff.length} membre(s) x {matrix.societes.length} societe(s) — Les droits s'appliquent a tous les modules isoles par societe
              </p>
            </>
          )}
        </div>
      )}

      {/* Tab: Historique des modifications */}
      {tab === "historique" && (
        <div data-testid="audit-section">
          {auditLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>
          ) : audit.length === 0 ? (
            <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl">
              <History className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400 text-sm">Aucune modification de role enregistree</p>
              <p className="text-[10px] text-slate-500 mt-1">Les changements de roles apparaitront ici</p>
            </div>
          ) : (
            <div className="space-y-2">
              {audit.map((entry) => {
                const oldInfo = ROLE_OPTIONS.find(r => r.value === entry.old_role) || ROLE_OPTIONS[3];
                const newInfo = ROLE_OPTIONS.find(r => r.value === entry.new_role) || ROLE_OPTIONS[3];
                const date = entry.changed_at ? new Date(entry.changed_at) : null;
                return (
                  <div key={entry.audit_id} className="bg-[#14101c] border border-white/5 rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`audit-entry-${entry.audit_id}`}>
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <History className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-white">{entry.user_name || entry.user_email}</span>
                        <span className="text-[10px] text-slate-500">sur</span>
                        <span className="text-xs font-medium flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry.societe_color || "#3b82f6" }} />
                          <span className="text-white">{entry.societe_name}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${oldInfo.bg}`}>{oldInfo.label}</span>
                        <ArrowRight className="w-3 h-3 text-slate-500" />
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${newInfo.bg}`}>{newInfo.label}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-slate-500">{date ? date.toLocaleDateString("fr-FR") : ""}</p>
                      <p className="text-[10px] text-slate-600">{date ? date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}</p>
                      <p className="text-[9px] text-slate-600 mt-0.5">par {entry.changed_by_name || entry.changed_by_email}</p>
                    </div>
                  </div>
                );
              })}
              <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {audit.length} modification(s) enregistree(s)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={v => { setShowDialog(v); if (!v) setEditSoc(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto mx-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#f7c935]" />
              {editSoc ? "Modifier la société" : "Nouvelle société"}
            </DialogTitle>
          </DialogHeader>
          {/* Billing notice when creating new societe and tenant has a quota */}
          {!editSoc && billing && billing.next_societe_extra_monthly > 0 && (
            <div className="mt-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-2" data-testid="billing-extra-notice">
              <DollarSign className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <div className="text-amber-300 font-bold">+{billing.next_societe_extra_monthly} EUR / mois (HT)</div>
                <div className="text-[10px] text-amber-200/80 mt-1">
                  Vous avez {billing.societe_count} societe{billing.societe_count > 1 ? "s" : ""} sur {billing.included_quota} incluse{billing.included_quota > 1 ? "s" : ""} dans votre offre <strong>{billing.offer_name}</strong>. La creation de cette societe ajoutera {billing.next_societe_extra_monthly} EUR/mois a votre prochaine facture.
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4 mt-2">
            {/* Identité */}
            <fieldset className="space-y-2">
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-1">Identité</legend>
              <Input value={form.name} onChange={e => F("name", e.target.value)} placeholder="Nom de la société *" className="bg-[#0b0811] border-white/10 text-white" data-testid="soc-name-input" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select value={form.pays} onValueChange={v => { F("pays", v); F("forme_juridique", ""); /* reset forme on pays change */ }}>
                  <SelectTrigger data-testid="soc-pays-select" className="bg-[#0b0811] border-white/10 text-white text-sm h-10">
                    <SelectValue placeholder="Pays" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`soc-pays-option-${c.code}`}>
                        {c.flag} {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.forme_juridique || "__none__"} onValueChange={v => F("forme_juridique", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="soc-forme-select" className="bg-[#0b0811] border-white/10 text-white text-sm h-10">
                    <SelectValue placeholder="Forme juridique" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                    <SelectItem value="__none__" className="text-xs text-slate-500 focus:bg-white/10 focus:text-white">— Choisir —</SelectItem>
                    {getLegalForms(form.pays).map(f => (
                      <SelectItem key={f} value={f} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`soc-forme-option-${f.replace(/[^A-Za-z0-9]+/g, '_')}`}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input type="number" value={form.capital} onChange={e => F("capital", parseFloat(e.target.value) || 0)} placeholder="Capital social" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <Input value={form.dirigeant} onChange={e => F("dirigeant", e.target.value)} placeholder="Dirigeant / Gérant" className="bg-[#0b0811] border-white/10 text-white text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={form.activite} onChange={e => F("activite", e.target.value)} placeholder="Activité principale" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">Date de création de la société</label>
                  <DateField value={form.date_creation_societe} onChange={(v) => F("date_creation_societe", v)} testId="soc-date-creation" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">Type de société</label>
                  <select value={form.type} onChange={e => F("type", e.target.value)} className="w-full bg-[#0b0811] border border-white/10 rounded-md text-white text-sm px-3 py-2" data-testid="soc-type-select">
                    <option value="filiale">Filiale</option>
                    <option value="holding">Holding</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">Société mère</label>
                  <select value={form.parent_societe_id} onChange={e => F("parent_societe_id", e.target.value)} className="w-full bg-[#0b0811] border border-white/10 rounded-md text-white text-sm px-3 py-2" data-testid="soc-parent-select">
                    <option value="">— Aucune —</option>
                    {societes.filter(s => !editSoc || s.societe_id !== editSoc.societe_id).map(s => (
                      <option key={s.societe_id} value={s.societe_id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Numéros */}
            <fieldset className="space-y-2">
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-1">Identification fiscale</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={form.siren} onChange={e => F("siren", e.target.value)} placeholder="SIREN (9 chiffres)" maxLength={9} className="bg-[#0b0811] border-white/10 text-white text-sm" data-testid="soc-siren-input" />
                <Input value={form.siret} onChange={e => F("siret", e.target.value)} placeholder="SIRET (14 chiffres)" maxLength={14} className="bg-[#0b0811] border-white/10 text-white text-sm" />
              </div>
              <Input value={form.tva_intra} onChange={e => F("tva_intra", e.target.value)} placeholder="TVA Intracommunautaire" className="bg-[#0b0811] border-white/10 text-white text-sm" />
            </fieldset>

            {/* Coordonnées (sans pays maintenant en haut) */}
            <fieldset className="space-y-2">
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-1">Coordonnées</legend>
              <Input value={form.adresse} onChange={e => F("adresse", e.target.value)} placeholder="Adresse" className="bg-[#0b0811] border-white/10 text-white text-sm" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={form.code_postal} onChange={e => F("code_postal", e.target.value)} placeholder="Code postal" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <Input value={form.ville} onChange={e => F("ville", e.target.value)} placeholder="Ville" className="bg-[#0b0811] border-white/10 text-white text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={form.email} onChange={e => F("email", e.target.value)} placeholder="Email" type="email" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <Input value={form.telephone} onChange={e => F("telephone", e.target.value)} placeholder="Téléphone" className="bg-[#0b0811] border-white/10 text-white text-sm" />
              </div>
            </fieldset>

            {/* Modules actives — Independance par societe */}
            <fieldset>
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-2 flex items-center gap-1.5"><Layers className="w-3 h-3" /> Modules actives pour cette societe</legend>
              <p className="text-[10px] text-slate-500 mb-3">Cochez les modules disponibles pour cette societe. Agenda et messages restent toujours actifs cross-societe.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="soc-modules-grid">
                {SOCIETE_MODULES.map(m => {
                  const Icon = MODULE_ICONS[m.icon] || Layers;
                  const checked = !!form.enabled_modules?.[m.key];
                  return (
                    <button key={m.key} type="button" onClick={() => F("enabled_modules", { ...form.enabled_modules, [m.key]: !checked })}
                      className={`flex items-start gap-2 p-3 rounded-lg border text-left transition-colors ${checked ? "border-[#f7c935]/50 bg-[#f7c935]/5" : "border-white/10 hover:border-white/20"}`}
                      style={{ background: checked ? undefined : "#0b0811" }}
                      data-testid={`soc-module-${m.key}`}
                    >
                      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${checked ? "text-[#f7c935]" : "text-slate-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold ${checked ? "text-[#f7c935]" : "text-slate-300"}`}>{m.label}</div>
                        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{m.desc}</div>
                      </div>
                      <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 ${checked ? "bg-[#f7c935] border-[#f7c935]" : "border-white/20"}`} />
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Devise */}
            <fieldset>
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-2">Devise</legend>
              <div className="flex gap-2">
                {DEVISES.map(d => (
                  <button key={d.code} onClick={() => F("devise", d.code)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium transition-all ${
                      form.devise === d.code
                        ? "bg-[#f7c935]/10 text-[#f7c935] border border-[#f7c935]/30"
                        : "bg-[#0b0811] text-slate-500 border border-white/5 hover:border-white/10"
                    }`}
                    data-testid={`devise-${d.code}`}>
                    <Globe className="w-3.5 h-3.5" />
                    {d.code}
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Identité visuelle — builder par société */}
            <fieldset>
              <legend className="text-[10px] text-[#f7c935] uppercase font-bold mb-2 flex items-center gap-1.5"><Palette className="w-3 h-3" /> Identité visuelle (logo & couleurs)</legend>
              <p className="text-[10px] text-slate-500 mb-2">Le logo et la couleur de la societe sont utilises sur la plateforme (selecteur, bandeaux) et sur les documents exportes (previsionnel banque...).</p>
              <div className="flex items-center gap-3 mb-3">
                {form.logo ? (
                  <div className="relative">
                    <img src={form.logo} alt="logo" className="w-14 h-14 rounded-xl object-contain bg-white/5 border border-white/10" data-testid="soc-logo-preview" />
                    <button type="button" onClick={() => F("logo", "")}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"
                      data-testid="soc-logo-remove">
                      <Trash2 className="w-2.5 h-2.5 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center border border-dashed border-white/15" style={{ background: `${form.color}10` }}>
                    <Building2 className="w-6 h-6" style={{ color: form.color }} />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-xs px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:border-white/25 inline-block">
                    {form.logo ? "Changer le logo" : "Importer un logo"}
                  </span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 1_500_000) { alert("Image trop volumineuse (max 1.5 Mo)"); return; }
                      const reader = new FileReader();
                      reader.onload = () => F("logo", reader.result);
                      reader.readAsDataURL(file);
                      e.target.value = "";
                    }}
                    data-testid="soc-logo-input" />
                </label>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                {COLORS.map(c => (
                  <button key={c} onClick={() => F("color", c)}
                    className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#14101c] scale-110" : "hover:scale-105"}`}
                    style={{ background: c }} />
                ))}
                <div className="flex items-center gap-1.5 ml-1 pl-2 border-l border-white/10">
                  <input type="color" value={form.color || "#3b82f6"} onChange={e => F("color", e.target.value)}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-transparent cursor-pointer" title="Couleur personnalisee"
                    data-testid="soc-color-custom" />
                  <span className="text-[10px] text-slate-500 font-mono">{form.color}</span>
                </div>
              </div>
            </fieldset>

            <Button onClick={save} disabled={saving || !form.name.trim()} className="w-full bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-societe-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editSoc ? "Modifier" : "Creer la societe"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ─── Dashboard vue d'ensemble multi-societe ─────────────────
const fmtEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, (mm || 1) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

function OverviewDashboard({ data }) {
  const { totals, societes, month } = data;
  return (
    <div className="mb-6 space-y-3" data-testid="overview-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overview-group-totals">
        <KpiCard label="CA Encaisse" value={fmtEur(totals.ca_total)} sub={`${totals.societes_count} societe${totals.societes_count > 1 ? "s" : ""}`} color="#10b981" testId="overview-ca-total" />
        <KpiCard label="Depenses" value={fmtEur(totals.depenses_total)} sub={monthLabel(month)} color="#ef4444" testId="overview-dep-total" />
        <KpiCard label={totals.marge_total >= 0 ? "Marge brute" : "Marge brute (-)"} value={fmtEur(totals.marge_total)} sub="CA - Depenses" color={totals.marge_total >= 0 ? "#f7c935" : "#f97316"} testId="overview-marge-total" />
        <KpiCard label="Encaissements en retard" value={totals.late_total > 0 ? `${totals.late_total}` : "0"} sub={totals.late_total > 0 ? "alerte" : "a jour"} color={totals.late_total > 0 ? "#ef4444" : "#10b981"} testId="overview-late-total" />
      </div>

      <div className="rounded-xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vue par societe — {monthLabel(month)}</h3>
          <span className="text-[10px] text-slate-500">{societes.length} societe{societes.length > 1 ? "s" : ""}</span>
        </div>
        <div className="p-3 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
          {societes.map(s => (
            <SocieteOverviewCard key={s.societe_id} s={s} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, testId }) {
  return (
    <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-xl font-black tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function SocieteOverviewCard({ s }) {
  const moduleColors = { mon_espace: "#94a3b8", force_de_vente: "#3b82f6", blitzz_accompaniment: "#8b5cf6", gestion_activite: "#10b981", formation: "#f59e0b" };
  const moduleLabels = { mon_espace: "Mon espace", force_de_vente: "Force de vente", blitzz_accompaniment: "Blitzz", gestion_activite: "Gestion", formation: "Formation" };
  return (
    <div className="rounded-lg border p-3 transition-colors hover:border-white/20" style={{ background: "#0b0811", borderColor: "rgba(255,255,255,0.06)" }} data-testid={`overview-societe-${s.societe_id}`}>
      <div className="flex items-start gap-2 mb-3">
        {s.logo ? (
          <img src={s.logo} alt="" className="w-8 h-8 rounded-lg object-contain flex-shrink-0 bg-white/5 border border-white/5" />
        ) : (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: s.color || "#10b981" }}>
            <Building2 className="w-4 h-4 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h4 className="text-sm font-bold truncate" style={{ color: "var(--text-primary)" }}>{s.name}</h4>
            {s.is_default && <Star className="w-3 h-3 text-[#f7c935] flex-shrink-0" fill="#f7c935" />}
          </div>
          {s.siren && <div className="text-[9px] text-slate-500 truncate">SIREN {s.siren}</div>}
        </div>
        {s.late_count > 0 && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 flex-shrink-0" data-testid={`overview-late-badge-${s.societe_id}`}>
            {s.late_count} retard{s.late_count > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">CA</div>
          <div className="text-xs font-bold tabular-nums" style={{ color: "#10b981" }}>{fmtEur(s.ca_ttc)}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Dep.</div>
          <div className="text-xs font-bold tabular-nums" style={{ color: "#ef4444" }}>{fmtEur(s.depenses_total)}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Marge</div>
          <div className="text-xs font-bold tabular-nums" style={{ color: s.marge_brute >= 0 ? "#f7c935" : "#f97316" }}>{fmtEur(s.marge_brute)}</div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {s.module_count === 0 ? (
          <span className="text-[9px] text-slate-500 italic">Aucun module active</span>
        ) : (
          s.active_modules.map(k => (
            <span key={k} className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: `${moduleColors[k] || "#94a3b8"}20`, color: moduleColors[k] || "#94a3b8" }} data-testid={`overview-module-${s.societe_id}-${k}`}>
              {moduleLabels[k] || k}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
